import { useEffect, useState } from 'react';
import SafeIcon from './SafeIcon';
import PauseDialog from './PauseDialog';
import BusinessEditor from './BusinessEditor';
import ConnectSheet from './ConnectSheet';
import { AiConnectForm, HelperGuestScreen, HelperInvitePanel } from './AiConnect';
import { channelFormats } from '../store/campaigns';
import { useApp, timeLabel, stampLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { postStatusHoldsDailySlot, useDemo, useWorkspaceData } from '../store/DemoStore';
import { DEMO_TODAY } from '../store/seed';
import { onAccount, preferencesFor } from '../store/seed';
import type { Account, Member, NoticeChannel, NoticeEvent } from '../store/seed';
import './SettingsPage.css';
import { aiProviderName } from './aiProviders';

const SECTION_SCROLL_DELAY_MS = 80;
const DEMO_YEAR = 2026;
const NOVEMBER = 10;
const DEFAULT_POSTING_TIMEZONE = 'Europe/Warsaw';
const OTHER_DISPLAY_TIMEZONES = ['Europe/London', 'Europe/Helsinki', 'America/New_York'];
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MINIMUM_INVITEE_NAME_LENGTH = 2;

const settingsSections: [string, string][] = [
  ['people', 'People'],
  ['business', 'Business details'],
  ['connections', 'Connections'],
  ['ai', 'AI connection'],
  ['notifications', 'Notifications'],
  ['personal', 'Your preferences'],
  ['agency', 'Your agency'],
];

const notificationEvents: [NoticeEvent, string, string][] = [
  ['review', 'Posts ready for review', 'When AMY has a draft or a new version for you.'],
  ['changes', 'Requested changes are ready', 'When a version you asked to change comes back.'],
  ['published', 'Published', 'When a post goes out.'],
  [
    'failed',
    'Something needs you',
    'A failed or partial publication, missing material or a question from AMY.',
  ],
  ['connection', 'Connection problems', 'An account needs reconnecting.'],
  ['weekly', 'Weekly summary', 'Monday morning: what went out and what is planned.'],
];
const notificationChannels: [NoticeChannel, string][] = [
  ['app', 'In the app'],
  ['email', 'Email'],
  ['push', 'Phone'],
];

const isPersonalProfile = (account: Account) => /personal/i.test(account.type);
const firstName = (fullName: string) => fullName.split(' ')[0];
const readableTimezone = (timezone: string) => timezone.replace('_', ' ');
const connectInvitationKey = (account: Account) => `connect-${account.id}`;

function accountHealthStatusClass(health: Account['health']) {
  if (health === 'Connected') return 'published';
  if (health === 'Needs reconnecting') return 'review';
  return 'neutral';
}

function scrollToSettingsSection(sectionId: string) {
  window.setTimeout(
    () =>
      document
        .getElementById(`settings-${sectionId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    SECTION_SCROLL_DELAY_MS
  );
}

export default function SettingsPage() {
  const [pauseDialog, setPauseDialog] = useState<'pause' | 'resume' | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [managedAccount, setManagedAccount] = useState<Account | null>(null);
  const [accountToConnect, setAccountToConnect] = useState<Account | 'new' | null>(null);
  const [guestConnectAccount, setGuestConnectAccount] = useState<Account | null>(null);
  const [aiKeyFormOpen, setAiKeyFormOpen] = useState(false);
  const [confirmingAiRemoval, setConfirmingAiRemoval] = useState(false);
  const [helperGuestId, setHelperGuestId] = useState<string | null>(null);
  const { translate, readOnlyProps, language, setLanguage } = useApp();
  const { route, actor, role, notify, navigate } = useUi();
  const { commands, state } = useDemo();
  const { workspace, settings, accounts, members, staff, posts, aiConnection } = useWorkspaceData(
    route.workspaceId
  );
  useEffect(() => {
    if (route.id) scrollToSettingsSection(route.id);
  }, [route.id]);
  const isOwner = role === 'owner';
  const isAgency = role === 'agency';
  const ownerOnly = isOwner
    ? {}
    : {
        disabled: true,
        title: translate('Only {name} can change this', { name: workspace.owner }),
      };
  const scheduledCount = state.posts.filter(
    (post) => post.workspaceId === workspace.id && post.status === 'Scheduled'
  ).length;
  const isMainContributor = (member: Member) =>
    member.role === 'Contributor' && member.name === workspace.contributor;
  const memberCanApprove = (member: Member) =>
    member.role === 'Owner' ||
    (isMainContributor(member) ? settings.contributorCanApprove : !!member.canApprove);
  const memberCanStop = (member: Member) =>
    member.role === 'Owner' ||
    (isMainContributor(member) ? settings.contributorCanStop : !!member.canStop);
  const memberCanEditBrand = (member: Member) =>
    member.role === 'Owner' ||
    (isMainContributor(member) ? !!settings.contributorCanEditBrand : !!member.canEditBrand);
  const toggleApprovePermission = (member: Member) =>
    isMainContributor(member)
      ? commands.setPermission(
          workspace.id,
          'contributorCanApprove',
          !settings.contributorCanApprove
        )
      : commands.setMemberPermission(member.id, 'canApprove', !member.canApprove);
  const toggleStopPermission = (member: Member) =>
    isMainContributor(member)
      ? commands.setPermission(workspace.id, 'contributorCanStop', !settings.contributorCanStop)
      : commands.setMemberPermission(member.id, 'canStop', !member.canStop);
  const toggleEditBrandPermission = (member: Member) =>
    isMainContributor(member)
      ? commands.setWorkspacePrefs(workspace.id, {
          contributorCanEditBrand: !settings.contributorCanEditBrand,
        })
      : commands.setMemberPermission(member.id, 'canEditBrand', !member.canEditBrand);
  const activeMembers = members.filter((member) => member.status === 'Active');
  const pendingMembers = members.filter((member) => member.status !== 'Active');
  const assignedStaff = staff.filter((staffMember) =>
    staffMember.workspaces.includes(workspace.id)
  );
  const mainContact = staff.find((staffMember) => staffMember.lead?.includes(workspace.id));
  const myPreferences = preferencesFor(settings, actor.name);
  const myNotificationChoices = myPreferences.notify;
  const workspaceTimezoneLabel = readableTimezone(
    settings.postingTimezone ?? DEFAULT_POSTING_TIMEZONE
  );
  const isOwnerOrAgency = isOwner || isAgency;

  function renderPersonalProfileConnectAction(account: Account) {
    const invitationKey = connectInvitationKey(account);
    const invitationSentAt = state.reminders[invitationKey];
    if (account.owner === actor.name)
      return (
        <button
          className="button small primary"
          onClick={() => setAccountToConnect(account)}
          {...readOnlyProps}
        >
          {translate('Connect my profile')}
        </button>
      );
    if (invitationSentAt)
      return (
        <span className="invite-pending">
          <span className="status review">
            {translate('Invitation sent {when}', {
              when: invitationSentAt,
            })}
          </span>
          <button
            className="button small"
            onClick={() => {
              commands.remind(invitationKey);
              notify(
                translate('Invitation sent again to {name} (simulated)', {
                  name: account.owner,
                })
              );
            }}
            {...readOnlyProps}
          >
            {translate('Resend')}
          </button>
          <button
            className="button small ghost"
            onClick={() => {
              commands.clearReminder(invitationKey);
              notify(translate('Invitation withdrawn'));
            }}
            {...readOnlyProps}
          >
            {translate('Withdraw')}
          </button>
          <button
            className="button small ghost"
            title={translate('Demo only')}
            onClick={() => setGuestConnectAccount(account)}
          >
            {translate('Open their link (demo)')}
          </button>
        </span>
      );
    return (
      <button
        className="button small"
        onClick={() => {
          commands.remind(invitationKey);
          notify(
            translate('Connect-only invitation sent to {name} (simulated)', {
              name: account.owner,
            })
          );
        }}
        {...readOnlyProps}
      >
        {translate('Invite to connect')}
      </button>
    );
  }

  return (
    <div className="content-stack settings-page">
      <div>
        <h1>{translate('Settings')}</h1>
        <p className="subtitle">
          {workspace.name} · {translate('People, permissions and connected accounts.')}
        </p>
        <nav className="settings-jump" aria-label={translate('Sections')}>
          {settingsSections.map(([id, sectionLabel]) => (
            <button
              key={id}
              className="link-button"
              onClick={() => navigate({ page: 'settings', id }, { replace: true })}
            >
              {translate(sectionLabel)}
            </button>
          ))}
        </nav>
      </div>

      <div className="panel" id="settings-people">
        <div className="panel-header">
          <h2>{translate('People & permissions')}</h2>
          <button
            className="button primary"
            onClick={() => setInviteOpen(true)}
            {...readOnlyProps}
            {...ownerOnly}
          >
            <SafeIcon name="FiUserPlus" />
            {translate('Invite contributor')}
          </button>
        </div>
        {activeMembers.map((member) => (
          <div className="checklist-item permission-person" key={member.id}>
            <div className="agency-mark">{member.name[0]}</div>
            <div className="permission-person-body">
              <div className="permission-person-heading">
                <div className="permission-person-details">
                  <strong>{member.name}</strong>
                  <div className="muted">{member.email}</div>
                </div>
                <span className="tag">{translate(member.role)}</span>
              </div>
              {member.role === 'Owner' ? (
                <p className="muted small-print">
                  {translate(
                    'The owner approves posts, stops posts and manages people and accounts.'
                  )}
                </p>
              ) : (
                <div className="permission-controls permission-controls-editable">
                  <div className="permission-control">
                    <div>
                      <strong>{translate('Can approve posts')}</strong>
                      <div className="muted">
                        {memberCanApprove(member)
                          ? translate('{name} can approve content for publishing.', {
                              name: firstName(member.name),
                            })
                          : translate('{name} can review and comment, but cannot approve.', {
                              name: firstName(member.name),
                            })}
                      </div>
                    </div>
                    <button
                      className={`toggle ${memberCanApprove(member) ? 'on' : ''}`}
                      onClick={() => toggleApprovePermission(member)}
                      aria-label={translate('Toggle post approvals for {name}', {
                        name: member.name,
                      })}
                      aria-pressed={memberCanApprove(member)}
                      {...readOnlyProps}
                      {...ownerOnly}
                    >
                      <span />
                    </button>
                  </div>
                  <div className="permission-control">
                    <div>
                      <strong>{translate('Can stop posts')}</strong>
                      <div className="muted">
                        {memberCanStop(member)
                          ? translate('{name} can stop a scheduled post.', {
                              name: firstName(member.name),
                            })
                          : translate('{name} cannot stop scheduled posts.', {
                              name: firstName(member.name),
                            })}
                      </div>
                    </div>
                    <button
                      className={`toggle ${memberCanStop(member) ? 'on' : ''}`}
                      onClick={() => toggleStopPermission(member)}
                      aria-label={translate('Toggle stopping posts for {name}', {
                        name: member.name,
                      })}
                      aria-pressed={memberCanStop(member)}
                      {...readOnlyProps}
                      {...ownerOnly}
                    >
                      <span />
                    </button>
                  </div>
                  <div className="permission-control">
                    <div>
                      <strong>{translate('Can edit brand drafts')}</strong>
                      <div className="muted">
                        {memberCanEditBrand(member)
                          ? translate(
                              '{name} can change the brand kit draft and business details. Publishing a new brand version stays with the owner.',
                              { name: firstName(member.name) }
                            )
                          : translate('{name} can view the brand kit but not change it.', {
                              name: firstName(member.name),
                            })}
                      </div>
                    </div>
                    <button
                      className={`toggle ${memberCanEditBrand(member) ? 'on' : ''}`}
                      onClick={() => toggleEditBrandPermission(member)}
                      aria-label={translate('Toggle brand drafts for {name}', {
                        name: member.name,
                      })}
                      aria-pressed={memberCanEditBrand(member)}
                      {...readOnlyProps}
                      {...ownerOnly}
                    >
                      <span />
                    </button>
                  </div>
                  {!isMainContributor(member) && isOwner && (
                    <button
                      className="button small ghost"
                      onClick={() => {
                        commands.removeMember(member.id, actor);
                        notify(translate('{name} no longer has access.', { name: member.name }));
                      }}
                    >
                      {translate('Remove access')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {pendingMembers.length > 0 && (
          <>
            <h3>{translate('Invitations')}</h3>
            {pendingMembers.map((member) => (
              <div className="checklist-item invite-row" key={member.id}>
                <SafeIcon name={member.status === 'Expired' ? 'FiAlertCircle' : 'FiMail'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>{member.name}</strong> · <span className="muted">{member.email}</span>
                  <div className="muted small-print">
                    {member.status === 'Expired'
                      ? translate('Invitation expired · sent {when} by {name}', {
                          when: timeLabel(member.invitedAt ?? '', language, translate),
                          name: member.invitedBy ?? workspace.owner,
                        })
                      : translate('Invited {when} by {name} · expires in 7 days', {
                          when: timeLabel(member.invitedAt ?? '', language, translate),
                          name: member.invitedBy ?? workspace.owner,
                        })}
                  </div>
                </div>
                <span className={`status ${member.status === 'Expired' ? 'input' : 'review'}`}>
                  {translate(member.status === 'Expired' ? 'Expired' : 'Invited')}
                </span>
                <span className="button-row">
                  <button
                    className="button small"
                    onClick={() => {
                      commands.resendInvite(member.id, actor);
                      notify(
                        translate('Invitation sent again to {email} (simulated)', {
                          email: member.email,
                        })
                      );
                    }}
                    {...readOnlyProps}
                    {...ownerOnly}
                  >
                    {translate('Resend')}
                  </button>
                  <button
                    className="button small ghost"
                    onClick={() => {
                      commands.revokeInvite(member.id);
                      notify(translate('Invitation withdrawn'));
                    }}
                    {...readOnlyProps}
                    {...ownerOnly}
                  >
                    {translate('Withdraw')}
                  </button>
                  {member.status === 'Invited' && (
                    <button
                      className="button small ghost"
                      title={translate('Demo only')}
                      onClick={() => {
                        commands.acceptInvite(member.id);
                        notify(
                          translate('{name} accepted the invitation (simulated)', {
                            name: member.name,
                          })
                        );
                      }}
                    >
                      {translate('Simulate: accepted')}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="panel" id="settings-effective">
        <h2>{translate('Who can do what')}</h2>
        <div className="table-wrap">
          <table className="data-table effective-table">
            <thead>
              <tr>
                <th>{translate('Person')}</th>
                <th>{translate('Review and comment')}</th>
                <th>{translate('Approve')}</th>
                <th>{translate('Stop scheduled posts')}</th>
                <th>{translate('Edit brand drafts')}</th>
                <th>{translate('Move posts')}</th>
                <th>{translate('People and accounts')}</th>
              </tr>
            </thead>
            <tbody>
              {activeMembers.map((member) => (
                <tr key={member.id}>
                  <td>
                    <strong>{member.name}</strong>
                    <div className="muted small-print">{translate(member.role)}</div>
                  </td>
                  <td>✓</td>
                  <td>{memberCanApprove(member) ? '✓' : '—'}</td>
                  <td>{memberCanStop(member) ? '✓' : '—'}</td>
                  <td>{memberCanEditBrand(member) ? '✓' : '—'}</td>
                  <td>{translate('Suggests a time')}</td>
                  <td>{member.role === 'Owner' ? '✓' : '—'}</td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>Hakusana</strong>
                  <div className="muted small-print">{translate('Agency staff')}</div>
                </td>
                <td>✓</td>
                <td>—</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>{translate('Accounts only')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="muted small-print">
          {translate(
            'Each permission is separate: approving posts does not include stopping them or publishing a new brand version. Clients approve their own posts; the agency prepares, moves and stops posts, and helps with accounts.'
          )}
        </p>
      </div>

      <div className="panel" id="settings-business">
        <h2>{translate('Business details')}</h2>
        <BusinessEditor />
      </div>

      <div className="panel" id="settings-connections">
        <div className="panel-header">
          <h2>{translate('Connections')}</h2>
          <button
            className="button"
            onClick={() => setAccountToConnect('new')}
            {...readOnlyProps}
            disabled={readOnlyProps.disabled || role === 'contributor'}
          >
            <SafeIcon name="FiPlus" />
            {translate('Connect an account')}
          </button>
        </div>
        {accounts.map((account) => {
          const publishableFormats = channelFormats[account.network as keyof typeof channelFormats]
            .map((format) => translate(format))
            .join(', ');
          return (
            <div className="checklist-item" key={account.id}>
              <SafeIcon name="FiLink" />
              <div style={{ flex: 1 }}>
                <strong>{account.name}</strong>
                <div className="muted">
                  {account.network} · {translate(account.type)} ·{' '}
                  {translate(
                    account.health === 'Connected' ? 'Connected by {name}' : 'Owner: {name}',
                    {
                      name: account.owner,
                    }
                  )}
                  {account.expires && account.health !== 'Connected'
                    ? ` · ${translate('expired {date}', { date: timeLabel(account.expires, language, translate) })}`
                    : ''}
                </div>
                <div className="muted small-print">
                  {isPersonalProfile(account)
                    ? translate('Personal profile · connected by its owner')
                    : `${translate('Can publish')}: ${publishableFormats}`}
                </div>
              </div>
              <span className={`status ${accountHealthStatusClass(account.health)}`}>
                {translate(account.health)}
              </span>
              {account.health === 'Needs reconnecting' &&
                (isOwnerOrAgency ? (
                  <button
                    className="button small primary"
                    onClick={() => setAccountToConnect(account)}
                    {...readOnlyProps}
                  >
                    {translate('Reconnect')}
                  </button>
                ) : (
                  <span className="muted small-print">
                    {translate('Only {name} can reconnect this account', { name: account.owner })}
                  </span>
                ))}
              {isPersonalProfile(account) &&
                account.health === 'Not connected' &&
                renderPersonalProfileConnectAction(account)}
              <button className="button small" onClick={() => setManagedAccount(account)}>
                {translate('Manage')}
              </button>
            </div>
          );
        })}
      </div>

      <div className="panel" id="settings-ai">
        <h2>{translate('AI connection')}</h2>
        {aiConnection.status === 'connected' ? (
          <div className="checklist-item">
            <SafeIcon name="FiCpu" />
            <div style={{ flex: 1 }}>
              <strong>
                {aiProviderName(aiConnection.provider)} · {translate(aiConnection.model ?? '')}
              </strong>
              <div className="muted">
                {translate('Connected by {name} · last test {when}', {
                  name: aiConnection.connectedBy ?? '',
                  when: stampLabel(aiConnection.lastTest ?? '', language),
                })}
                {isOwner && aiConnection.keyEnding
                  ? ` · ${translate('key ending {k}', { k: aiConnection.keyEnding })}`
                  : ''}
              </div>
              {aiConnection.error && (
                <div className="field-warn">
                  {translate('The last test did not pass. Replace the key or test again.')}
                </div>
              )}
            </div>
            <span className="status published">{translate('Connected')}</span>
          </div>
        ) : (
          <div className="checklist-item">
            <SafeIcon name="FiCpu" />
            <div style={{ flex: 1 }}>
              <strong>{translate('No AI connected')}</strong>
              <div className="muted">
                {translate(
                  aiConnection.trial
                    ? 'AMY drafts on Hakusana’s trial allowance until you connect your own AI key. Your provider then bills you separately from AMY.'
                    : 'AMY is not drafting: connect an AI key to continue. Requested changes wait until then.'
                )}
              </div>
            </div>
            <span className={`status ${aiConnection.status === 'error' ? 'input' : 'neutral'}`}>
              {translate(aiConnection.status === 'error' ? 'Test failed' : 'Not connected')}
            </span>
          </div>
        )}
        {!isOwner ? (
          <p className="muted small-print">
            {translate(
              'Only {name} manages the AI key. Keys are never shown to anyone, including the agency.',
              { name: workspace.owner }
            )}
          </p>
        ) : (
          <>
            <div className="button-row">
              {aiConnection.status === 'connected' && (
                <button
                  className="button"
                  onClick={() => {
                    commands.retestAi(workspace.id);
                    notify(translate('Test passed. AI is working.'));
                  }}
                  {...readOnlyProps}
                >
                  <SafeIcon name="FiZap" />
                  {translate('Test again')}
                </button>
              )}
              <button
                className="button"
                onClick={() => setAiKeyFormOpen(!aiKeyFormOpen)}
                {...readOnlyProps}
              >
                <SafeIcon name="FiKey" />
                {translate(aiConnection.status === 'connected' ? 'Replace key' : 'Connect AI')}
              </button>
              {aiConnection.status === 'connected' && (
                <button
                  className="button danger-outline"
                  onClick={() => setConfirmingAiRemoval(true)}
                  {...readOnlyProps}
                >
                  {translate('Remove key')}
                </button>
              )}
            </div>
            {confirmingAiRemoval && (
              <div className="notice-banner warn">
                <SafeIcon name="FiAlertTriangle" />
                <div>
                  <strong>{translate('Remove the AI key?')}</strong>
                  <div>
                    {translate(
                      'AMY stops drafting new posts and revisions. Scheduled and approved posts still publish. You can connect a key again at any time.'
                    )}
                  </div>
                  <div className="button-row">
                    <button
                      className="button small danger"
                      onClick={() => {
                        commands.removeAi(workspace.id, actor);
                        setConfirmingAiRemoval(false);
                        notify(
                          translate('AI key removed. AMY stops drafting until a key is connected.'),
                          'warn'
                        );
                      }}
                    >
                      {translate('Remove key')}
                    </button>
                    <button className="button small" onClick={() => setConfirmingAiRemoval(false)}>
                      {translate('Cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {aiKeyFormOpen && (
              <div className="panel inset">
                <AiConnectForm compact onConnected={() => setAiKeyFormOpen(false)} />
              </div>
            )}
            {aiConnection.status !== 'connected' && (
              <HelperInvitePanel onGuest={setHelperGuestId} />
            )}
          </>
        )}
      </div>

      <div className="panel" id="settings-notifications">
        <h2>{translate('Notifications')}</h2>
        <p className="muted small-print">
          {translate('{name}’s own choices for {ws}. Others in the workspace choose theirs.', {
            name: actor.name,
            ws: workspace.name,
          })}
        </p>
        <div className="table-wrap">
          <table className="data-table notify-table">
            <thead>
              <tr>
                <th>{translate('When')}</th>
                {notificationChannels.map(([channel, channelLabel]) => (
                  <th key={channel}>{translate(channelLabel)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {notificationEvents.map(([noticeEvent, label, hint]) => (
                <tr key={noticeEvent}>
                  <td>
                    <strong>{translate(label)}</strong>
                    <div className="muted small-print">{translate(hint)}</div>
                  </td>
                  {notificationChannels.map(([channel, channelLabel]) => {
                    const isChosen = !!myNotificationChoices?.[noticeEvent]?.includes(channel);
                    const isAlwaysShownInApp = noticeEvent === 'failed' && channel === 'app';
                    return (
                      <td key={channel} data-label={translate(channelLabel)}>
                        <input
                          type="checkbox"
                          checked={isChosen || isAlwaysShownInApp}
                          disabled={isAlwaysShownInApp || readOnlyProps.disabled}
                          title={
                            isAlwaysShownInApp ? translate('Always shown in the app') : undefined
                          }
                          aria-label={translate('{event} by {channel}', {
                            event: translate(label),
                            channel: translate(channelLabel),
                          })}
                          onChange={(event) =>
                            commands.setNotify(
                              workspace.id,
                              noticeEvent,
                              channel,
                              event.target.checked,
                              actor.name
                            )
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <label className="brand-check">
          <input
            type="checkbox"
            checked={myPreferences.quietHours}
            onChange={(event) =>
              commands.setPersonalPrefs(workspace.id, actor.name, {
                quietHours: event.target.checked,
              })
            }
            {...readOnlyProps}
          />{' '}
          {translate(
            'Quiet hours: no phone notifications 21:00–07:00, except problems with posts going out today'
          )}
        </label>
      </div>

      <div className="panel" id="settings-personal">
        <h2>{translate('Your preferences')}</h2>
        <p className="muted small-print">
          {translate('These apply to you only, not to the workspace.')}
        </p>
        <div className="business-grid">
          <label className="wizard-field">
            {translate('Interface language')}
            <select
              className="select-field"
              value={language}
              onChange={(event) => setLanguage?.(event.target.value as typeof language)}
            >
              <option value="en">English</option>
              <option value="pl">Polski</option>
            </select>
            <span className="muted small-print">
              {translate('Posts keep their own content language.')}
            </span>
          </label>
          <label className="wizard-field">
            {translate('Show times in')}
            <select
              className="select-field"
              value={myPreferences.displayTimezone ?? 'workspace'}
              onChange={(event) =>
                commands.setPersonalPrefs(workspace.id, actor.name, {
                  displayTimezone: event.target.value,
                })
              }
              {...readOnlyProps}
            >
              <option value="workspace">
                {translate('Workspace time ({zone})', {
                  zone: workspaceTimezoneLabel,
                })}
              </option>
              {OTHER_DISPLAY_TIMEZONES.map((timezone) => (
                <option key={timezone} value={timezone}>
                  {readableTimezone(timezone)}
                </option>
              ))}
            </select>
            <span className="muted small-print">
              {translate('Posts still go out at the workspace time: {zone}.', {
                zone: workspaceTimezoneLabel,
              })}
            </span>
          </label>
        </div>
        <label className="brand-check">
          <input
            type="checkbox"
            checked={!!myPreferences.dailyDigest}
            onChange={(event) =>
              commands.setPersonalPrefs(workspace.id, actor.name, {
                dailyDigest: event.target.checked,
              })
            }
            {...readOnlyProps}
          />{' '}
          {translate('Daily digest email at 08:00 instead of one email per post')}
        </label>
      </div>

      <div className="panel" id="settings-agency">
        <h2>{translate('Your agency')}</h2>
        {mainContact ? (
          <div className="checklist-item">
            <div className="agency-mark">{mainContact.name[0]}</div>
            <div style={{ flex: 1 }}>
              <strong>{mainContact.name}</strong>
              <div className="muted">
                {translate(mainContact.role)} · {translate('Your main contact')} ·{' '}
                {mainContact.email}
              </div>
            </div>
            <span className="muted small-print">
              {translate('Usually replies within one working day')}
            </span>
          </div>
        ) : (
          <p className="muted">{translate('No main contact is assigned yet.')}</p>
        )}
        {assignedStaff
          .filter((staffMember) => staffMember !== mainContact)
          .map((staffMember) => (
            <div className="checklist-item" key={staffMember.id}>
              <div className="agency-mark">{staffMember.name[0]}</div>
              <div style={{ flex: 1 }}>
                <strong>{staffMember.name}</strong>
                <div className="muted">{translate(staffMember.role)}</div>
              </div>
            </div>
          ))}
        {isAgency && !mainContact && (
          <p className="field-warn">
            {translate(
              '{ws} has no main contact now. Choose one below; until then the client sees “No main contact”.',
              { ws: workspace.name }
            )}
          </p>
        )}
        {isAgency && (
          <div className="staff-assign">
            <h3>{translate('Staff assigned to {ws}', { ws: workspace.name })}</h3>
            <p className="muted small-print">
              {translate(
                'Agency only. Assigned staff see this workspace and its tasks; the main contact is shown to the client.'
              )}
            </p>
            {staff.map((staffMember) => {
              const isAssigned = staffMember.workspaces.includes(workspace.id);
              const isLead = !!staffMember.lead?.includes(workspace.id);
              return (
                <div className="checklist-item" key={staffMember.id}>
                  <label className="brand-check compact">
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      onChange={(event) =>
                        commands.assignStaff(staffMember.id, workspace.id, event.target.checked)
                      }
                    />{' '}
                    <strong>{staffMember.name}</strong>
                  </label>
                  <span className="muted" style={{ flex: 1 }}>
                    {translate(staffMember.role)}
                  </span>
                  {isLead ? (
                    <span className="status published">{translate('Main contact')}</span>
                  ) : (
                    <button
                      className="button small"
                      onClick={() => commands.setLead(staffMember.id, workspace.id)}
                    >
                      {translate('Make main contact')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel-header">
          <div>
            <h2>{translate('Pause all publishing')}</h2>
            <p className="muted">
              {translate('Nothing is published while paused. {n} scheduled posts would wait.', {
                n: scheduledCount,
              })}
            </p>
          </div>
          <button
            className={`toggle ${settings.paused ? 'on' : ''}`}
            aria-pressed={settings.paused}
            onClick={() => setPauseDialog(settings.paused ? 'resume' : 'pause')}
            aria-label={translate('Pause all publishing')}
            {...readOnlyProps}
            {...(isOwnerOrAgency ? {} : ownerOnly)}
          >
            <span />
          </button>
        </div>
        {settings.paused && (
          <div className="notice-banner">
            <SafeIcon name="FiAlertTriangle" />
            {translate('Publishing paused by {name}', {
              name: settings.pausedBy ?? workspace.owner,
            })}
          </div>
        )}
      </div>

      {pauseDialog && <PauseDialog mode={pauseDialog} onClose={() => setPauseDialog(null)} />}
      {inviteOpen && <InviteSheet onClose={() => setInviteOpen(false)} />}
      {managedAccount && (
        <AccountSheet
          onConnect={(accountToReconnect) => {
            setManagedAccount(null);
            setAccountToConnect(accountToReconnect);
          }}
          account={
            accounts.find((candidate) => candidate.id === managedAccount.id) ?? managedAccount
          }
          posts={posts.filter(
            (post) =>
              onAccount(post, managedAccount) &&
              postStatusHoldsDailySlot(post.status) &&
              post.day >= DEMO_TODAY &&
              post.status !== 'Published'
          )}
          onClose={() => setManagedAccount(null)}
        />
      )}
      {accountToConnect && (
        <ConnectSheet
          account={accountToConnect === 'new' ? undefined : accountToConnect}
          onClose={() => setAccountToConnect(null)}
        />
      )}
      {guestConnectAccount && (
        <ConnectSheet
          account={guestConnectAccount}
          asPerson={guestConnectAccount.owner}
          onClose={() => setGuestConnectAccount(null)}
        />
      )}
      {helperGuestId && (
        <HelperGuestScreen helperId={helperGuestId} onClose={() => setHelperGuestId(null)} />
      )}
    </div>
  );
}

function InviteSheet({ onClose }: { onClose: () => void }) {
  const { translate } = useApp();
  const { route, actor, notify } = useUi();
  const { commands } = useDemo();
  const { workspace } = useWorkspaceData(route.workspaceId);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [canApprove, setCanApprove] = useState(false);
  const [canStop, setCanStop] = useState(false);
  const [error, setError] = useState('');
  const isEmailFormatValid = EMAIL_PATTERN.test(email.trim());
  const canSend = name.trim().length >= MINIMUM_INVITEE_NAME_LENGTH && isEmailFormatValid;
  const showEmailFormatError = !!email.trim() && !isEmailFormatValid;
  function send() {
    const inviteResult = commands.invite(
      workspace.id,
      name.trim(),
      email.trim(),
      { canApprove, canStop },
      actor
    );
    if (inviteResult === 'exists') {
      setError(
        translate('{email} already has access or a pending invitation.', { email: email.trim() })
      );
      return;
    }
    notify(
      translate('Invitation sent to {email} (simulated). It expires in 7 days.', {
        email: email.trim(),
      })
    );
    onClose();
  }
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer invite-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="invite-title">{translate('Invite contributor')}</h2>
            <div className="muted">{workspace.name}</div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <label className="wizard-field">
          {translate('Full name')}
          <input
            className="text-field"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </label>
        <label className="wizard-field">
          {translate('Email')}
          <input
            className="text-field"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setError('');
            }}
            aria-invalid={!!email && !isEmailFormatValid}
          />
          {error && (
            <span className="field-error" role="alert">
              {error}
            </span>
          )}
          {!error && showEmailFormatError && (
            <span className="field-error">
              {translate('Enter a full email address, like name@example.com.')}
            </span>
          )}
        </label>
        <fieldset className="wizard-channels">
          <legend>{translate('What can they do?')}</legend>
          <label className="brand-check">
            <input type="checkbox" checked disabled />{' '}
            {translate('Review posts, comment and request changes')}
          </label>
          <label className="brand-check">
            <input
              type="checkbox"
              checked={canApprove}
              onChange={(event) => setCanApprove(event.target.checked)}
            />{' '}
            {translate('Approve posts for publishing')}
          </label>
          <label className="brand-check">
            <input
              type="checkbox"
              checked={canStop}
              onChange={(event) => setCanStop(event.target.checked)}
            />{' '}
            {translate('Stop scheduled posts')}
          </label>
        </fieldset>
        <p className="muted small-print">
          {translate(
            'They get an email with a link that works for 7 days. You can change these permissions later.'
          )}
        </p>
        <div className="picker-footer">
          <button className="button" onClick={onClose}>
            {translate('Cancel')}
          </button>
          <button className="button primary" disabled={!canSend} onClick={send}>
            <SafeIcon name="FiSend" />
            {translate('Send invitation')}
          </button>
        </div>
      </section>
    </div>
  );
}

function AccountSheet({
  account,
  posts,
  onClose,
  onConnect,
}: {
  account: Account;
  posts: { id: string; title: string; day: number; time: string; status: string }[];
  onClose: () => void;
  onConnect: (account: Account) => void;
}) {
  const { translate, language, readOnlyProps } = useApp();
  const { actor, notify, role, navigate } = useUi();
  const { commands } = useDemo();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const isPersonal = isPersonalProfile(account);
  const shortDemoDate = (dayOfNovember: number) =>
    new Intl.DateTimeFormat(language, { weekday: 'short', day: 'numeric', month: 'short' }).format(
      new Date(DEMO_YEAR, NOVEMBER, dayOfNovember)
    );
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer account-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="account-title">{account.name}</h2>
            <div className="muted">
              {account.network} · {translate(account.type)}
            </div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <dl className="asset-facts">
          <div>
            <dt>{translate('Status')}</dt>
            <dd>{translate(account.health)}</dd>
          </div>
          <div>
            <dt>{translate(account.health === 'Connected' ? 'Connected by' : 'Owner')}</dt>
            <dd>{account.owner}</dd>
          </div>
          {account.connectedAt && (
            <div>
              <dt>{translate('Connected on')}</dt>
              <dd>{timeLabel(account.connectedAt, language, translate)}</dd>
            </div>
          )}
          {account.expires && (
            <div>
              <dt>{translate('Access expires')}</dt>
              <dd>{timeLabel(account.expires, language, translate)}</dd>
            </div>
          )}
        </dl>
        <h3>{translate('Upcoming posts on this account ({n})', { n: posts.length })}</h3>
        {posts.length === 0 ? (
          <p className="muted">{translate('Nothing planned for this account.')}</p>
        ) : (
          <ul className="bulk-list">
            {posts.map((post) => (
              <li key={post.id}>
                <SafeIcon name="FiCalendar" size={14} />
                <span>
                  <button
                    className="link-button"
                    onClick={() => {
                      onClose();
                      navigate({ page: 'post', id: post.id });
                    }}
                  >
                    {post.title}
                  </button>{' '}
                  · {shortDemoDate(post.day)} {post.time}
                </span>
              </li>
            ))}
          </ul>
        )}
        {account.health === 'Needs reconnecting' && (
          <div className="notice-banner warn">
            <SafeIcon name="FiAlertTriangle" />
            <span>
              {translate(
                'Access expired. Only {name} can reconnect, because the network asks the account owner to log in.',
                { name: account.owner }
              )}
            </span>
            {role !== 'contributor' && (
              <button
                className="button small primary"
                onClick={() => onConnect(account)}
                {...readOnlyProps}
              >
                {translate('Reconnect')}
              </button>
            )}
          </div>
        )}
        {account.health === 'Not connected' && (
          <div className="notice-banner">
            <SafeIcon name="FiInfo" />
            <span>
              {isPersonal
                ? translate(
                    'A personal profile can only be connected by its owner, {name}. Send them an invitation to connect.',
                    { name: account.owner }
                  )
                : translate('Not connected. Connect it to publish here.')}
            </span>
            {isPersonal ? (
              <button
                className="button small"
                onClick={() => {
                  commands.remind(connectInvitationKey(account));
                  notify(translate('Invite sent to {name} (simulated)', { name: account.owner }));
                }}
                {...readOnlyProps}
              >
                {translate('Invite to connect')}
              </button>
            ) : (
              role !== 'contributor' && (
                <button
                  className="button small primary"
                  onClick={() => onConnect(account)}
                  {...readOnlyProps}
                >
                  {translate('Connect')}
                </button>
              )
            )}
          </div>
        )}
        {account.health === 'Connected' && (
          <>
            <p className="muted small-print">
              {translate(
                'AMY publishes only what you approve. It never posts in your name without an approved post.'
              )}
            </p>
            {!confirmingDisconnect ? (
              <div className="button-row">
                {!isPersonal && (
                  <button
                    className="button"
                    onClick={() => onConnect(account)}
                    disabled={role === 'contributor'}
                    {...readOnlyProps}
                  >
                    <SafeIcon name="FiRepeat" />
                    {translate('Replace with another account')}
                  </button>
                )}
                <button
                  className="button danger-outline"
                  onClick={() => setConfirmingDisconnect(true)}
                  disabled={role === 'contributor'}
                  {...readOnlyProps}
                >
                  <SafeIcon name="FiLink2" />
                  {translate('Disconnect')}
                </button>
              </div>
            ) : (
              <div className="notice-banner warn">
                <SafeIcon name="FiAlertTriangle" />
                <span>
                  {translate(
                    '{n} upcoming posts on this account keep their approval but wait until it is connected again. Nothing is deleted.',
                    { n: posts.length }
                  )}
                </span>
                <button
                  className="button small danger"
                  onClick={() => {
                    const waitingPostCount = commands.disconnectAccount(account.id, actor);
                    notify(
                      translate('{account} disconnected. {n} scheduled posts wait.', {
                        account: account.name,
                        n: waitingPostCount,
                      }),
                      'warn'
                    );
                    onClose();
                  }}
                >
                  {translate('Disconnect')}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
